/**
 * Tunnels, as the map has them: most are not bored through a hill at all.
 *
 * OSM marks `tunnel` on any way that passes under something. Around a
 * Swedish town nearly all of them are a few tens of metres long: a railway
 * or a lane under a motorway's embankment, a footway through a building.
 * Dropped, as every tunnel was, the railway stopped dead at the motorway.
 *
 * - **Underpass.** A short tunnel with a road or railway passing over it is
 *   drawn on its own level, in a trough, and goes down to pass under the way
 *   over it, which runs on over it unbroken (`roadprofile.ts`), the trough
 *   roofed under it (`tunnelmesh.ts`). Split into a deck there instead, a
 *   motorway humped and stepped over each of the three tunnels the map has
 *   under it at Hålan. Only a railway under a road, which cannot dip, has
 *   the road bridged over it: the stretch above the tunnel becomes a deck,
 *   which the bridge code raises clear and builds a slab under.
 * - **Passage.** A short tunnel with nothing over it the map knows of, a way
 *   through a building, is drawn on the ground like any other.
 * - **Footway under a road.** A path or footway under a road or railway, a
 *   pedestrian underpass, goes down into a tube of its own. Bridged, the
 *   road over it rose 3.6 m for a footpath. It is bored, as:
 * - **Bored.** Anything longer goes through a hill or under a city: laid in
 *   a tube under the terrain, its floor straight between its portals and
 *   dipped under whatever crosses over it (`roadprofile.ts`, `tunnelmesh.ts`).
 * - **Trench.** A tunnel the laser ground model shows lying in an open cut,
 *   the land either side of it standing metres over it: a sunken motorway
 *   through an interchange, mapped as a tunnel because every road and track
 *   over it is on a bridge the map does not tag. The E6 through
 *   Olskroksmotet in Gothenburg (57.7148, 11.9954) is one, 300 m long under
 *   ten ways. Whatever its length it is an underpass, and every way over it
 *   is bridged, as over a railway: a trough on the cut's floor, decks across.
 */

import type { RoadLine } from './roads.js';
import { joinSplitLines } from './roads.js';
import { ROAD_STYLE } from '../sources/shortbread.js';

/** Longest tunnel taken as an underpass, metres: under a motorway's two carriageways and their verges. */
export const UNDERPASS_MAX = 150;
/** Longest tunnel with nothing over it drawn on the ground, metres: a passage through a building. */
export const PASSAGE_MAX = 60;
/** Clearance either side of the way under, along the deck over it, metres. */
const UNDERPASS_CLEAR = 3;
/** How much higher the land beside a tunnel's way must stand than along it for the way to lie in an open cut, metres. */
const TRENCH_DEPTH = 2.5;
/** How far out past the way's kerb the land beside it is looked at, metres: the cut's wall may be some way off. */
const TRENCH_OUT = [3, 9, 16];
/** Between samples along the way, metres, and how far in from each portal the first is taken. */
const TRENCH_STEP = 6;
const TRENCH_IN = 10;
/** The share of a tunnel network's length that must lie in a cut for it to be a trench. */
const TRENCH_SHARE = 0.6;
/** Shallowest angle, as its sine, a deck is laid out for; shallower, it would run along the way under. */
const SKEW_SIN = 0.3;
/** A piece of road left shorter than this beside a deck goes into the deck, metres. */
const STUB = 3;
/**
 * Longest open piece between two underpasses taken as part of them, metres:
 * the gap between a motorway's carriageways, open to the sky, which the map
 * has as road between two tunnels.
 */
const UNDERPASS_GAP = 20;

/**
 * Clear height inside a tunnel, floor to ceiling, by `ROAD_STYLE`: an
 * electrified railway's, a road's with its signs and lamps, a tram's, a
 * footway's.
 */
export function tunnelHeadroom(style: number): number {
  if (style === ROAD_STYLE.rail) return 6.8;
  if (style === ROAD_STYLE.tram) return 4.6;
  if (style <= ROAD_STYLE.service) return 5.4;
  if (style === ROAD_STYLE.track) return 4.2;
  return 2.8;
}
/** The tunnel's roof, ceiling to the underside of whatever is over it, metres. */
export const TUNNEL_ROOF = 0.8;

export interface Tunnels {
  /** The roads to draw: the map's, the way over each underpass split into bridge and road, and the tunnels drawn on their own level. */
  roads: RoadLine[];
  /** Tunnels through the ground, flagged `tunnel`, steps left out. */
  bored: RoadLine[];
}

/** Can a tunnel pass under this way: a road, a railway or a tramway, not a path. */
const carries = (line: RoadLine) => {
  const style = line.style ?? 0;
  return style <= ROAD_STYLE.service || style === ROAD_STYLE.rail || style === ROAD_STYLE.tram;
};

export function resolveTunnels(
  roads: readonly RoadLine[],
  tunnels: readonly RoadLine[],
  /** The laser ground model's land, where there is one: it tells a trench from a tunnel. Without it, no trenches. */
  ground?: (x: number, z: number) => number,
): Tunnels {
  const out: RoadLine[] = [];
  const bored: RoadLine[] = [];
  /** Per road, the stretches along it, metres, that become a deck. */
  const decks = new Map<number, Array<[number, number]>>();
  const boxes = roads.map(r => boxOf(r.points));
  // Classification must see the whole tunnel, not each OSM feature. Otherwise
  // a short internal section becomes a surface passage and a false portal.
  const joined = joinSplitLines(tunnels.map(t => ({ ...t, tunnel: true, bridge: false })));
  // Width changes and branches cannot be one ribbon, but still belong to
  // one underground network. Classify those pieces by the connected length.
  const parent = joined.map((_, i) => i);
  const root = (i: number): number => {
    while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; }
    return i;
  };
  const familyOf = (t: RoadLine) => t.style === ROAD_STYLE.rail ? 'rail' : carries(t) ? 'road' : 'path';
  const endpoints = new Map<string, number>();
  joined.forEach((t, i) => {
    if (t.style === ROAD_STYLE.steps) return;
    const p = t.points;
    for (const k of [0, p.length - 2]) {
      if (k < 0) continue;
      const key = `${familyOf(t)}:${p[k].toFixed(2)},${p[k + 1].toFixed(2)}`;
      const other = endpoints.get(key);
      if (other !== undefined) parent[root(i)] = root(other);
      else endpoints.set(key, i);
    }
  });
  // And a tunnel ending on another's side: a crossover between a railway's
  // two bores, a branch off a road tunnel, which the map does not split the
  // other at. On its own it was a short network, an underpass drawn on the
  // surface: the crossover in Gårdatunneln stood on the hill over the bores,
  // 130 m of track at 40 m with the ground filled 20 m up to it.
  const tunnelBoxes = joined.map(t => boxOf(t.points));
  joined.forEach((t, i) => {
    if (t.style === ROAD_STYLE.steps) return;
    const p = t.points;
    for (const k of [0, p.length - 2]) {
      if (k < 0) continue;
      const x = p[k], z = p[k + 1];
      joined.forEach((u, j) => {
        if (j === i || root(j) === root(i) || u.style === ROAD_STYLE.steps || familyOf(u) !== familyOf(t)) return;
        const near = Math.max(1, u.halfWidth), b = tunnelBoxes[j];
        if (x < b[0] - near || x > b[2] + near || z < b[1] - near || z > b[3] + near) return;
        if (distanceToLine(u.points, x, z) <= near) parent[root(i)] = root(j);
      });
    }
  });
  const lengths = new Map<number, number>();
  joined.forEach((t, i) => lengths.set(root(i), (lengths.get(root(i)) ?? 0) + lengthOf(t.points)));
  // By network: a trench's carriageways and links lie in one cut.
  const cuts = new Map<number, number>();
  if (ground !== undefined) joined.forEach((t, i) => cuts.set(root(i), (cuts.get(root(i)) ?? 0) + cutLength(t, ground)));
  for (const [ti, tunnel] of joined.entries()) {
    if (tunnel.style === ROAD_STYLE.steps) continue;
    const length = lengths.get(root(ti)) ?? lengthOf(tunnel.points);
    const trench = carries(tunnel) && (cuts.get(root(ti)) ?? 0) >= TRENCH_SHARE * length;
    const over = length <= UNDERPASS_MAX || trench ? overWays(tunnel, roads, boxes) : [];
    // A footway under a road or the tracks dips under them in a tube of its
    // own: bridged instead, the road or railway over it humped 3.6 m for a
    // path. Steps down into one are left out; the footway meets the ground.
    if (over.length > 0 && !carries(tunnel)) {
      bored.push({ ...tunnel, tunnel: true, bridge: false });
    } else if (over.length > 0) {
      for (const { road, along, sin } of over) {
        // Over a road's underpass a road runs on whole, the trough roofed
        // under it; over a railway's, or a trench, it is bridged.
        if (tunnel.style !== ROAD_STYLE.rail && !trench) break;
        const line = roads[road];
        if (line.bridge === true) continue;
        const s = Math.max(SKEW_SIN, sin);
        const half = (tunnel.halfWidth + UNDERPASS_CLEAR) / s + line.halfWidth * Math.sqrt(1 - s * s) / s;
        const list = decks.get(road) ?? [];
        // Across the whole cut, out to where the land stands up again either
        // side: the laser ground model has no ground under a bridge and
        // fills the gap in low, so a deck laid over the way alone stood 2 m
        // in the cut, and the road climbed out of it at 30 %.
        const span = trench && ground !== undefined ? cutSpan(line.points, along, half, ground) : [half, half];
        list.push([along - span[0], along + span[1]]);
        decks.set(road, list);
      }
      out.push({ ...tunnel, tunnel: false, bridge: false, underpass: true });
    } else if (length <= PASSAGE_MAX) {
      out.push({ ...tunnel, tunnel: false, bridge: false });
    } else {
      bored.push({ ...tunnel, tunnel: true, bridge: false });
    }
  }
  roads.forEach((line, i) => {
    const ranges = decks.get(i);
    if (ranges === undefined) out.push(line);
    else out.push(...splitForDecks(line, ranges));
  });
  // The open gap between two underpasses on one way is the same passage: on
  // the underpass's level, and joined into one line with them (`roads.ts`).
  // A road of its own there, it was solved apart and ended a metre over the
  // next underpass's floor.
  const portals = out.filter(l => l.underpass === true).flatMap(l => {
    const p = l.points, n = p.length / 2;
    return [{ x: p[0], z: p[1], style: l.style }, { x: p[n * 2 - 2], z: p[n * 2 - 1], style: l.style }];
  });
  const atPortal = (x: number, z: number, style: number | undefined) => portals.some(e => e.style === style && Math.hypot(e.x - x, e.z - z) < 1);
  out.forEach((line, i) => {
    if (line.underpass === true || line.bridge === true || line.tunnel === true) return;
    const p = line.points, n = p.length / 2;
    if (n < 2 || lengthOf(p) > UNDERPASS_GAP) return;
    if (atPortal(p[0], p[1], line.style) && atPortal(p[n * 2 - 2], p[n * 2 - 1], line.style)) out[i] = { ...line, underpass: true };
  });
  // A path into a footway's tunnel under a road runs straight out of its
  // portal, between the wing walls and down the ramp: drawn a few degrees
  // off the tunnel's line, as the map often has it, it ran a metre to one
  // side of the portal's slab by the walls' end.
  for (const tunnel of bored) {
    if (carries(tunnel)) continue;
    const t = tunnel.points, m = t.length / 2;
    if (m < 2) continue;
    for (const [ex, ez, px, pz] of [[t[0], t[1], t[2], t[3]], [t[m * 2 - 2], t[m * 2 - 1], t[m * 2 - 4], t[m * 2 - 3]]]) {
      const len = Math.hypot(ex - px, ez - pz);
      if (len < 1e-6) continue;
      const ux = (ex - px) / len, uz = (ez - pz) / len;
      out.forEach((line, i) => {
        if (line.bridge === true || line.tunnel === true || carries(line)) return;
        const p = line.points, n = p.length / 2;
        if (Math.hypot(p[0] - ex, p[1] - ez) < 1) out[i] = { ...line, points: straightenFrom(p, ex, ez, ux, uz) };
        else if (Math.hypot(p[n * 2 - 2] - ex, p[n * 2 - 1] - ez) < 1) out[i] = { ...line, points: reversed(straightenFrom(reversed(p), ex, ez, ux, uz)) };
      });
    }
  }
  return { roads: out, bored };
}

/**
 * How much of a tunnel's way lies in an open cut, metres: sampled along it,
 * in from its portals, where the land `TRENCH_OUT` past either kerb stands
 * `TRENCH_DEPTH` or more over the land on the way itself. A bored tunnel's
 * land is the hill's, the same on the way and beside it; a cut's walls
 * stand over its floor. The laser ground model has the floor of a cut, and
 * the land either side as it is; under a bridge it has the cut's floor
 * too, the deck taken out.
 */
function cutLength(tunnel: RoadLine, ground: (x: number, z: number) => number): number {
  const p = tunnel.points;
  const total = lengthOf(p);
  if (total < TRENCH_IN * 2 + TRENCH_STEP) return 0;
  let cut = 0;
  for (let s = TRENCH_IN; s <= total - TRENCH_IN; s += TRENCH_STEP) {
    const { x, z, ux, uz } = alongLine(p, s);
    const here = ground(x, z);
    let left = -Infinity, right = -Infinity;
    for (const d of TRENCH_OUT) {
      const out = tunnel.halfWidth + d;
      left = Math.max(left, ground(x - uz * out, z + ux * out));
      right = Math.max(right, ground(x + uz * out, z - ux * out));
    }
    if (Math.min(left, right) - here >= TRENCH_DEPTH) cut += TRENCH_STEP;
  }
  return cut;
}

/** Furthest a trench's deck is run on either way past the way under, looking for the cut's top, metres. */
const TRENCH_SPAN = 60;

/**
 * How far a deck over a trench runs either way from its crossing at `along`:
 * at least `half`, else on to where the land is `TRENCH_DEPTH` over the
 * cut's floor at the crossing, and the clearance past that.
 */
function cutSpan(p: Float64Array, along: number, half: number, ground: (x: number, z: number) => number): [number, number] {
  const at = alongLine(p, along);
  const floor = ground(at.x, at.z);
  const out = (dir: 1 | -1): number => {
    for (let d = half; d <= TRENCH_SPAN; d += 2) {
      const q = alongLine(p, along + dir * d);
      if (ground(q.x, q.z) >= floor + TRENCH_DEPTH) return d + UNDERPASS_CLEAR;
    }
    return half;
  };
  return [out(-1), out(1)];
}

/** The point `s` metres along a polyline, and the unit direction of the segment it is on. */
function alongLine(p: Float64Array, s: number): { x: number; z: number; ux: number; uz: number } {
  let travelled = 0;
  for (let i = 0; i + 3 < p.length; i += 2) {
    const ax = p[i], az = p[i + 1], dx = p[i + 2] - ax, dz = p[i + 3] - az;
    const segment = Math.hypot(dx, dz);
    if (segment < 1e-9) continue;
    if (travelled + segment >= s || i + 4 >= p.length) {
      const t = Math.min(1, Math.max(0, (s - travelled) / segment));
      return { x: ax + dx * t, z: az + dz * t, ux: dx / segment, uz: dz / segment };
    }
    travelled += segment;
  }
  return { x: p[0], z: p[1], ux: 1, uz: 0 };
}

/** How far out of a portal a path is laid straight on the tunnel's line, metres, and over how far more it eases back onto its own. */
const STRAIGHT_OUT = 10;
const STRAIGHT_EASE = 10;

/**
 * A polyline starting at a portal (x, z), its first `STRAIGHT_OUT` metres laid
 * along (ux, uz) and eased back onto its own line over `STRAIGHT_EASE` more.
 * Unless it turns away from that line straight off, more than 45°: then it is
 * a path along the portal's face, not out of it.
 */
function straightenFrom(p: Float64Array, x: number, z: number, ux: number, uz: number): Float64Array {
  const n = p.length / 2;
  const arc = new Float64Array(n);
  for (let k = 1; k < n; k++) arc[k] = arc[k - 1] + Math.hypot(p[k * 2] - p[k * 2 - 2], p[k * 2 + 1] - p[k * 2 - 1]);
  const total = arc[n - 1];
  if (total < 1) return p;
  const at = (s: number): [number, number] => {
    let k = 0;
    while (k + 2 < n && arc[k + 1] < s) k++;
    const span = arc[k + 1] - arc[k], t = span > 0 ? Math.min(1, Math.max(0, (s - arc[k]) / span)) : 0;
    return [p[k * 2] + (p[k * 2 + 2] - p[k * 2]) * t, p[k * 2 + 1] + (p[k * 2 + 3] - p[k * 2 + 1]) * t];
  };
  const [ax, az] = at(Math.min(total, STRAIGHT_OUT));
  const off = Math.hypot(ax - x, az - z);
  if (off < 1e-6 || ((ax - x) * ux + (az - z) * uz) / off < Math.SQRT1_2) return p;
  const stops = new Set<number>(arc);
  for (let s = 0; s <= STRAIGHT_OUT + STRAIGHT_EASE; s += 2.5) if (s < total) stops.add(s);
  const out: number[] = [];
  for (const s of [...stops].sort((a, b) => a - b)) {
    const [qx, qz] = at(s);
    const u = Math.min(1, Math.max(0, (s - STRAIGHT_OUT) / STRAIGHT_EASE)), w = u * u * (3 - 2 * u);
    out.push(x + ux * s + (qx - x - ux * s) * w, z + uz * s + (qz - z - uz * s) * w);
  }
  return Float64Array.from(out);
}

function reversed(p: Float64Array): Float64Array {
  const out = new Float64Array(p.length);
  for (let k = 0; k < p.length; k += 2) { out[k] = p[p.length - 2 - k]; out[k + 1] = p[p.length - 1 - k]; }
  return out;
}

/**
 * The roads passing over a tunnel: crossing its centreline between its
 * ends, where it is under them. A road meeting the tunnel at its portal is
 * the way it runs on into, not one over it. Where along each road, and at
 * what angle, as its sine.
 */
function overWays(tunnel: RoadLine, roads: readonly RoadLine[], boxes: ReadonlyArray<readonly number[]>): Array<{ road: number; along: number; sin: number }> {
  const t = tunnel.points;
  const tLength = lengthOf(t);
  const tb = boxOf(t);
  const found: Array<{ road: number; along: number; sin: number }> = [];
  roads.forEach((line, ri) => {
    if (line.tunnel === true || !carries(line)) return;
    const b = boxes[ri];
    if (b[0] > tb[2] || b[2] < tb[0] || b[1] > tb[3] || b[3] < tb[1]) return;
    const p = line.points;
    let travelled = 0;
    for (let i = 0; i + 3 < p.length; i += 2) {
      const ax = p[i], az = p[i + 1], bx = p[i + 2], bz = p[i + 3];
      const segment = Math.hypot(bx - ax, bz - az);
      let tTravelled = 0;
      for (let j = 0; j + 3 < t.length; j += 2) {
        const cx = t[j], cz = t[j + 1], dx = t[j + 2], dz = t[j + 3];
        const tSegment = Math.hypot(dx - cx, dz - cz);
        const hit = intersect(ax, az, bx, bz, cx, cz, dx, dz);
        if (hit !== undefined) {
          const inTunnel = tTravelled + tSegment * hit.u;
          if (inTunnel > 1 && inTunnel < tLength - 1) {
            found.push({ road: ri, along: travelled + segment * hit.t, sin: Math.abs(hit.sin) });
          }
        }
        tTravelled += tSegment;
      }
      travelled += segment;
    }
  });
  return found;
}

/** A road cut into pieces, those within `ranges` (metres along it) bridges. */
export function splitForDecks(line: RoadLine, ranges: ReadonlyArray<readonly [number, number]>): RoadLine[] {
  const length = lengthOf(line.points);
  // Merged, clamped, and run on to an end rather than leave a stub there.
  const merged: Array<[number, number]> = [];
  for (const [a, b] of [...ranges].sort((x, y) => x[0] - y[0])) {
    let lo = Math.max(0, a), hi = Math.min(length, b);
    if (lo < STUB) lo = 0;
    if (hi > length - STUB) hi = length;
    if (hi - lo < 1) continue;
    const last = merged[merged.length - 1];
    if (last !== undefined && lo <= last[1] + STUB) last[1] = Math.max(last[1], hi);
    else merged.push([lo, hi]);
  }
  if (merged.length === 0) return [line];
  const cuts: Array<{ at: number; bridge: boolean }> = [];
  let from = 0;
  for (const [lo, hi] of merged) {
    if (lo > from) cuts.push({ at: lo, bridge: false });
    cuts.push({ at: hi, bridge: true });
    from = hi;
  }
  if (from < length) cuts.push({ at: length, bridge: false });
  const pieces: RoadLine[] = [];
  let start = 0;
  for (const { at, bridge } of cuts) {
    const points = slice(line.points, start, at);
    if (points.length >= 4) pieces.push({ ...line, points, bridge: bridge || line.bridge === true });
    start = at;
  }
  return pieces;
}

/** The part of a polyline between two distances along it. */
function slice(p: Float64Array, from: number, to: number): Float64Array {
  const out: number[] = [];
  let travelled = 0;
  for (let i = 0; i + 3 < p.length; i += 2) {
    const ax = p[i], az = p[i + 1], bx = p[i + 2], bz = p[i + 3];
    const segment = Math.hypot(bx - ax, bz - az);
    const s0 = travelled, s1 = travelled + segment;
    travelled = s1;
    if (s1 < from || s0 > to || segment < 1e-9) continue;
    const at = (s: number) => [ax + (bx - ax) * (s - s0) / segment, az + (bz - az) * (s - s0) / segment];
    if (out.length === 0) out.push(...at(Math.max(from, s0)));
    const [ex, ez] = at(Math.min(to, s1));
    if (Math.hypot(ex - out[out.length - 2], ez - out[out.length - 1]) > 1e-6) out.push(ex, ez);
  }
  return Float64Array.from(out);
}

/** A polyline's bounds: min x, min z, max x, max z. */
function boxOf(p: Float64Array): number[] {
  const b = [Infinity, Infinity, -Infinity, -Infinity];
  for (let i = 0; i + 1 < p.length; i += 2) {
    b[0] = Math.min(b[0], p[i]); b[1] = Math.min(b[1], p[i + 1]);
    b[2] = Math.max(b[2], p[i]); b[3] = Math.max(b[3], p[i + 1]);
  }
  return b;
}

/** How far (x, z) is from a polyline, metres. */
function distanceToLine(p: Float64Array, x: number, z: number): number {
  let best = Infinity;
  for (let i = 0; i + 3 < p.length; i += 2) {
    const ax = p[i], az = p[i + 1], ux = p[i + 2] - ax, uz = p[i + 3] - az, len2 = ux * ux + uz * uz;
    const t = len2 > 0 ? Math.max(0, Math.min(1, ((x - ax) * ux + (z - az) * uz) / len2)) : 0;
    best = Math.min(best, Math.hypot(ax + ux * t - x, az + uz * t - z));
  }
  return best;
}

function lengthOf(p: Float64Array): number {
  let length = 0;
  for (let i = 0; i + 3 < p.length; i += 2) length += Math.hypot(p[i + 2] - p[i], p[i + 3] - p[i + 1]);
  return length;
}

function intersect(
  ax: number, az: number, bx: number, bz: number,
  cx: number, cz: number, dx: number, dz: number,
): { t: number; u: number; sin: number } | undefined {
  const rx = bx - ax, rz = bz - az, sx = dx - cx, sz = dz - cz;
  const denom = rx * sz - rz * sx;
  if (Math.abs(denom) < 1e-9) return undefined;
  const t = ((cx - ax) * sz - (cz - az) * sx) / denom;
  const u = ((cx - ax) * rz - (cz - az) * rx) / denom;
  if (t < 0 || t > 1 || u < 0 || u > 1) return undefined;
  return { t, u, sin: denom / (Math.hypot(rx, rz) * Math.hypot(sx, sz)) };
}
